import { Component, OnInit, HostListener } from '@angular/core'
import { Router } from '@angular/router'
import { Campaign, CampaignSource, ContractStatus, FirestoreStructure, InvoiceStatus, Subscription, subscriptionsPath } from '@parkupp/core'

/** Subscriptions that never actually started – excluded from booked value */
const NEVER_STARTED_STATUSES = [
    ContractStatus.new,
    ContractStatus.incomplete,
    ContractStatus.paymentInitiated,
    ContractStatus.paymentFailed,
    ContractStatus.merchantRejected,
    ContractStatus.expired,
    ContractStatus.pendingClientApproval,
]
import { CampaignService } from '../../../services/campaign.service'
import { GlobalService } from '../../../services/global.service'
import { environment } from '../../../../environments/environment'
import { ToastrService } from 'ngx-toastr'
import { collection, getDocs, query, where } from '@angular/fire/firestore'
import { Firestore } from '@angular/fire/firestore'

@Component({
    selector: 'app-admin-campaigns-list',
    templateUrl: './admin-campaigns-list.component.html',
    styleUrls: ['./admin-campaigns-list.component.scss'],
})
export class AdminCampaignsListComponent implements OnInit {
    campaigns: Campaign[] = []
    filteredCampaignsByStatus: Campaign[] = []
    loading = true
    baseUrl = environment.host || window.location.origin
    showQRModal = false
    qrCodeUrl = ''
    qrCodeCampaignName = ''
    showSourceLinksModal = false
    showShareModal = false
    selectedCampaign: Campaign | null = null
    campaignSources = Object.values(CampaignSource)
    statusFilter: 'active' | 'deleted' = 'active'
    /** Booked contract value (VAT incl.) of subscriptions that actually started */
    campaignValues: { [campaignName: string]: number } = {}
    /** Paid invoice revenue (VAT incl.) */
    campaignRevenue: { [campaignName: string]: number } = {}

    constructor(private campaignService: CampaignService, private router: Router, private toastr: ToastrService, private firestore: Firestore, public globalService: GlobalService) {}

    // Close context menu when clicking elsewhere
    @HostListener('document:click')
    onDocumentClick(): void {
        this.globalService.hideContextMenu()
    }

    ngOnInit(): void {
        this.loadCampaigns()
        this.loadCampaignValues()
    }

    loadCampaigns(): void {
        this.loading = true
        this.campaignService.getAll().subscribe({
            next: (campaigns) => {
                this.campaigns = campaigns.sort((a, b) => {
                    // Sort by createdAt descending (newest first)
                    if (a.createdAt && b.createdAt) {
                        return b.createdAt.toMillis() - a.createdAt.toMillis()
                    }
                    return 0
                })
                this.applyStatusFilter()
                this.loading = false
            },
            error: (error) => {
                console.error('Error loading campaigns:', error)
                this.loading = false
                this.toastr.error('Failed to load campaigns')
            },
        })
    }

    async loadCampaignValues(): Promise<void> {
        try {
            const [subscriptions, paidInvoices] = await Promise.all([
                getDocs(collection(this.firestore, subscriptionsPath())),
                getDocs(query(collection(this.firestore, FirestoreStructure.INVOICES), where('status', '==', InvoiceStatus.PAID))),
            ])

            const campaignValues: { [campaignName: string]: number } = {}
            subscriptions.docs.forEach((doc) => {
                const sub = doc.data() as Subscription
                if (!sub.p_campaign || NEVER_STARTED_STATUSES.includes(sub.status as ContractStatus)) return
                // A client cancellation before the first payment never started either
                if (sub.status === ContractStatus.clientCancelled && !sub.paymentsMade) return
                // totalContractValue already includes baysBooked via periodFee; convert cents to rands
                const value = new Subscription(sub).totalContractValue / 100
                campaignValues[sub.p_campaign] = (campaignValues[sub.p_campaign] || 0) + value
            })

            const campaignRevenue: { [campaignName: string]: number } = {}
            paidInvoices.docs.forEach((doc) => {
                const invoice = doc.data()
                if (!invoice['p_campaign']) return
                campaignRevenue[invoice['p_campaign']] = (campaignRevenue[invoice['p_campaign']] || 0) + (Number(invoice['amountTotalDue']) || 0)
            })

            this.campaignValues = campaignValues
            this.campaignRevenue = campaignRevenue
        } catch (error) {
            console.error('Error loading campaign values:', error)
        }
    }

    getCampaignRevenue(campaignName: string): number {
        return this.campaignRevenue[campaignName] || 0
    }

    getTotalCampaignRevenue(): number {
        return this.filteredCampaignsByStatus.reduce((sum, c) => sum + this.getCampaignRevenue(c.campaignName), 0)
    }

    getCampaignValue(campaignName: string): number {
        return this.campaignValues[campaignName] || 0
    }

    getActiveCampaignsCount(): number {
        return this.campaigns.filter((c) => c.isActive !== false).length
    }

    getArchivedCampaignsCount(): number {
        return this.campaigns.filter((c) => c.isActive === false).length
    }

    getTotalCampaignValue(): number {
        return Object.values(this.campaignValues).reduce((sum, val) => sum + val, 0)
    }

    getUniqueSourcesCount(): number {
        const sources = new Set(this.campaigns.map((c) => c.source))
        return sources.size
    }

    applyStatusFilter(): void {
        if (this.statusFilter === 'active') {
            this.filteredCampaignsByStatus = this.campaigns.filter((c) => c.isActive !== false)
        } else {
            this.filteredCampaignsByStatus = this.campaigns.filter((c) => c.isActive === false)
        }
    }

    changeStatusFilter(status: 'active' | 'deleted'): void {
        this.statusFilter = status
        this.applyStatusFilter()
    }

    createCampaign(): void {
        this.router.navigate(['/admin/campaigns/create'])
    }

    openShareModal(campaign: Campaign): void {
        this.selectedCampaign = campaign
        this.showShareModal = true
    }

    closeShareModal(): void {
        this.showShareModal = false
        this.selectedCampaign = null
    }

    copyLink(campaign?: Campaign): void {
        const targetCampaign = campaign || this.selectedCampaign
        if (!targetCampaign) return

        const url = targetCampaign.getFullUrl(this.baseUrl)

        // Try modern clipboard API first
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(url).then(
                () => {
                    this.toastr.success('Link copied to clipboard!')
                },
                (err) => {
                    console.error('Failed to copy link:', err)
                    this.fallbackCopy(url)
                }
            )
        } else {
            // Fallback for browsers that don't support clipboard API
            this.fallbackCopy(url)
        }
    }

    private fallbackCopy(text: string): void {
        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        textArea.style.top = '-999999px'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()

        try {
            const successful = document.execCommand('copy')
            if (successful) {
                this.toastr.success('Link copied to clipboard!')
            } else {
                this.toastr.error('Failed to copy link')
            }
        } catch (err) {
            console.error('Fallback copy failed:', err)
            this.toastr.error('Failed to copy link')
        }

        document.body.removeChild(textArea)
    }

    downloadQRCodeFromShare(): void {
        if (!this.selectedCampaign) return

        const svg = document.querySelector('.share-modal qrcode svg') as SVGElement
        if (svg) {
            // Get SVG as string
            const svgData = new XMLSerializer().serializeToString(svg)
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
            const url = URL.createObjectURL(svgBlob)
            const link = document.createElement('a')
            link.href = url
            link.download = `campaign-${this.selectedCampaign!.campaignName}-qrcode.svg`
            link.click()
            URL.revokeObjectURL(url)
            this.toastr.success('QR Code downloaded as SVG!')
        }
    }

    shareViaEmail(): void {
        if (!this.selectedCampaign) return

        const url = this.selectedCampaign.getFullUrl(this.baseUrl)
        const subject = encodeURIComponent(`Parking Opportunity`)
        const body = encodeURIComponent(`Hi,\n\nCheck out this parking opportunity:\n\n${url}\n\nBest regards`)
        window.location.href = `mailto:?subject=${subject}&body=${body}`
    }

    shareViaWhatsApp(): void {
        if (!this.selectedCampaign) return

        const url = this.selectedCampaign.getFullUrl(this.baseUrl)
        const message = encodeURIComponent(`Check out this parking opportunity: ${url}`)
        window.open(`https://wa.me/?text=${message}`, '_blank')
    }

    viewInReporting(campaign: Campaign): void {
        this.router.navigate(['/admin/dashboard'], {
            queryParams: {
                source: campaign.source,
                campaign: campaign.campaignName,
            },
        })
    }

    async softDeleteCampaign(campaign: Campaign): Promise<void> {
        if (confirm(`Are you sure you want to archive the campaign "${campaign.campaignName}"? The campaign will be hidden but historical data will be preserved.`)) {
            try {
                await this.campaignService.update(campaign.$key!, { isActive: false })
                this.toastr.success('Campaign archived successfully')
                this.loadCampaigns()
            } catch (error) {
                console.error('Error archiving campaign:', error)
                this.toastr.error('Failed to archive campaign')
            }
        }
    }

    async restoreCampaign(campaign: Campaign): Promise<void> {
        try {
            await this.campaignService.update(campaign.$key!, { isActive: true })
            this.toastr.success('Campaign restored successfully')
            this.loadCampaigns()
        } catch (error) {
            console.error('Error restoring campaign:', error)
            this.toastr.error('Failed to restore campaign')
        }
    }

    showSourceLinksHelp(): void {
        this.showSourceLinksModal = true
    }

    closeSourceLinksModal(): void {
        this.showSourceLinksModal = false
    }

    getSourceLink(source: CampaignSource): string {
        const url = new URL(this.baseUrl)
        url.searchParams.set('p_source', source)
        return url.toString()
    }

    copySourceLink(source: CampaignSource): void {
        const url = this.getSourceLink(source)

        // Try modern clipboard API first
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(url).then(
                () => {
                    this.toastr.success(`${source} source link copied!`)
                },
                (err) => {
                    console.error('Failed to copy link:', err)
                    this.fallbackCopy(url)
                }
            )
        } else {
            this.fallbackCopy(url)
        }
    }

    // Right-click context menu for campaign rows
    onCampaignContextMenu(event: MouseEvent, campaign: Campaign): void {
        event.preventDefault()

        const menuItems: any[] = [
            {
                label: 'Copy Link',
                icon: 'fa-solid fa-link',
                action: () => {
                    this.copyLink(campaign)
                    this.globalService.hideContextMenu()
                },
            },
            {
                label: 'Share',
                icon: 'fa-solid fa-share-nodes',
                action: () => {
                    this.openShareModal(campaign)
                    this.globalService.hideContextMenu()
                },
            },
            {
                label: 'View Stats',
                icon: 'fa-solid fa-chart-column',
                action: () => {
                    this.viewInReporting(campaign)
                    this.globalService.hideContextMenu()
                },
            },
        ]

        // Add archive/restore based on status
        if (this.statusFilter === 'active') {
            menuItems.push({
                label: 'Archive',
                icon: 'fa-solid fa-box-archive',
                action: () => {
                    this.softDeleteCampaign(campaign)
                    this.globalService.hideContextMenu()
                },
            })
        } else {
            menuItems.push({
                label: 'Restore',
                icon: 'fa-solid fa-rotate-left',
                action: () => {
                    this.restoreCampaign(campaign)
                    this.globalService.hideContextMenu()
                },
            })
        }

        this.globalService.contextMenuItems = menuItems
        this.globalService.showContextMenu(event)
    }
}
